// Build Line - the roster CSV, parsed in the browser.
//
// A DELIBERATE SECOND COPY of parseRoster() from src/lib/build/enrollment.ts,
// kept faithful field for field. The server is still the authority - it parses
// the same text again and its refusals are the ones that count - but the wizard
// needs the rows locally for two things it cannot ask the server for:
//
//   1. the preview an operator reads BEFORE they commit (how many mailboxes, how
//      many domains, which lines are malformed), and
//   2. the PERSONA expansion. Photos are supplied per identity - one face reused
//      across every domain that identity sends from - and the snapshot's
//      `overrides.photos.images` map is keyed by FINAL ADDRESS. The station does
//      no expansion and no persona matching: it reads the map it is given
//      (design 4.2). So the expansion has to happen here, before submit, or the
//      photos are silently addressed to nobody.
//
// If parseRoster() changes server-side, change this with it.

/** One roster line, exactly as the server records it. */
export interface RosterRow {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  group?: string;
}

export interface RosterIssue {
  path: string;
  message: string;
}

export interface ParsedRoster {
  rows: RosterRow[];
  issues: RosterIssue[];
}

/** RFC4180-ish: quoted fields, doubled quotes inside them. Written out rather
 *  than split on commas because a roster is pasted by a human and a password
 *  legitimately contains commas and quotes. */
function splitCsvLine(line: string, delim: string): string[] {
  // Tab-separated (a paste straight from Google Sheets or the Workspace
  // bulk-upload export). A tab can never appear inside a Sheets cell, so a
  // password with a comma or a double quote survives here where it would not
  // in the quote-aware CSV path below.
  if (delim === '\t') return line.split('\t').map((f) => f.trim());
  const out: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"' && field === '') {
      quoted = true;
      continue;
    }
    if (ch === delim) {
      out.push(field);
      field = '';
      continue;
    }
    field += ch;
  }
  out.push(field);
  return out.map((f) => f.trim());
}

/** `Email Address [Required]` -> `email address`, `First Name` -> `first name`. */
function headerKey(raw: string): string {
  return raw.replace(/\[[^\]]*\]/g, '').trim().toLowerCase();
}

const HEADER_ALIASES: Record<string, keyof RosterRow> = {
  'email address': 'email',
  email: 'email',
  'first name': 'first_name',
  first: 'first_name',
  first_name: 'first_name',
  'last name': 'last_name',
  last: 'last_name',
  last_name: 'last_name',
  password: 'password',
  group: 'group',
  'group tag': 'group',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The pasted roster in either shape the operators actually have: Google's bulk
 * upload export (`First Name [Required]`, ...) or the plain
 * `email,first,last,password[,group]` an operator types.
 *
 * Bad rows are REPORTED, never dropped: a roster that silently shrinks by two
 * lines is an order that silently ships two mailboxes short.
 */
export function parseRoster(raw: string): ParsedRoster {
  const rows: RosterRow[] = [];
  const issues: RosterIssue[] = [];
  const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) return { rows, issues: [{ path: 'roster_csv', message: 'the roster is empty' }] };

  // A Google Sheets paste is tab-separated; a typed or downloaded CSV is comma.
  // The delimiter is uniform across the paste, so detect it once from line 1.
  const delim = (lines[0] as string).includes('\t') ? '\t' : ',';
  const first = splitCsvLine(lines[0] as string, delim);
  const mapped = first.map((cell) => HEADER_ALIASES[headerKey(cell)]);
  const hasHeader = mapped.some((m) => m === 'email');
  const columns: (keyof RosterRow | undefined)[] = hasHeader
    ? mapped
    : ['email', 'first_name', 'last_name', 'password', 'group'];

  const seen = new Set<string>();
  for (let i = hasHeader ? 1 : 0; i < lines.length; i++) {
    const lineNo = i + 1;
    const cells = splitCsvLine(lines[i] as string, delim);
    const row: Record<string, string> = {};
    columns.forEach((column, index) => {
      if (column) row[column] = cells[index] ?? '';
    });

    const email = (row.email ?? '').toLowerCase();
    const missing = (['first_name', 'last_name', 'password'] as const).filter((f) => !row[f]);
    if (!EMAIL_RE.test(email)) {
      issues.push({ path: `roster_csv:${lineNo}`, message: `"${cells[0] ?? ''}" is not an email address` });
      continue;
    }
    if (missing.length > 0) {
      // Never quote the password back, even when it is the missing one.
      issues.push({ path: `roster_csv:${lineNo}`, message: `${email}: missing ${missing.join(', ')}` });
      continue;
    }
    if (seen.has(email)) {
      issues.push({ path: `roster_csv:${lineNo}`, message: `${email} appears twice` });
      continue;
    }
    seen.add(email);
    rows.push({
      email,
      first_name: row.first_name as string,
      last_name: row.last_name as string,
      password: row.password as string,
      ...(row.group ? { group: row.group } : {}),
    });
  }

  if (rows.length === 0 && issues.length === 0) {
    issues.push({ path: 'roster_csv', message: 'the roster has no usable rows' });
  }
  return { rows, issues };
}

/** Mailboxes per domain: what the allocator plans in, and the one roster shape
 *  the enrollment refuses outright. Mirrors rosterDomainIssues(). */
export const MAX_USERS_PER_DOMAIN = 18;

export function domainsOf(rows: RosterRow[]): string[] {
  return [...new Set(rows.map((r) => r.email.split('@')[1] ?? ''))].filter(Boolean).sort();
}

/** Domains carrying more mailboxes than one panel can hold. */
export function overloadedDomains(rows: RosterRow[]): { domain: string; count: number }[] {
  const perDomain = new Map<string, number>();
  for (const row of rows) {
    const domain = row.email.split('@')[1] ?? '';
    perDomain.set(domain, (perDomain.get(domain) ?? 0) + 1);
  }
  return [...perDomain]
    .filter(([, count]) => count > MAX_USERS_PER_DOMAIN)
    .map(([domain, count]) => ({ domain, count }));
}

/**
 * One PERSONA: an identity that sends from one or more addresses.
 *
 * Keyed by first + last name, because that is what "one face per identity"
 * means in the Cold Labs lineage - the same person appears as
 * `drew@askalpha.com` and `drew@askbeta.com`, and both mailboxes wear the same
 * photograph. The local parts are carried along and shown, because they are how
 * an operator recognises the row.
 */
export interface Persona {
  /** Stable key for React and for the photo map. Lowercased "first last". */
  key: string;
  first_name: string;
  last_name: string;
  /** Distinct local parts this identity sends from, for display. */
  locals: string[];
  /** Every final address this persona owns. The photo map is keyed by these. */
  emails: string[];
}

/** The distinct identities on a roster, in first-appearance order. */
export function personasOf(rows: RosterRow[]): Persona[] {
  const byKey = new Map<string, Persona>();
  for (const row of rows) {
    const key = `${row.first_name} ${row.last_name}`.trim().toLowerCase();
    const local = row.email.split('@')[0] ?? '';
    const existing = byKey.get(key);
    if (existing) {
      if (!existing.locals.includes(local)) existing.locals.push(local);
      existing.emails.push(row.email);
      continue;
    }
    byKey.set(key, {
      key,
      first_name: row.first_name,
      last_name: row.last_name,
      locals: [local],
      emails: [row.email],
    });
  }
  return [...byKey.values()];
}
